import type { ReviewNotePreviewView, ReviewNoteSourceLessonView, ReviewNoteView } from '@/api/lcReviewNote'

export function normalizeSourceLessonIds(ids: number[]): number[] {
  return [...new Set(ids)].sort((a, b) => a - b)
}

export function filterSelectableSourceLessonIds(
  ids: number[],
  lessons: ReviewNoteSourceLessonView[],
): number[] {
  const knownIds = new Set(lessons.map((lesson) => lesson.lessonId))
  const unavailableIds = new Set(
    lessons.filter((lesson) => lesson.selectable === false).map((lesson) => lesson.lessonId),
  )
  return normalizeSourceLessonIds(ids.filter((id) => knownIds.has(id) && !unavailableIds.has(id)))
}

export function getReviewNoteSourceCounts(lessons: ReviewNoteSourceLessonView[]) {
  return {
    chapterCount: new Set(lessons.filter((lesson) => lesson.chapterId != null).map((lesson) => lesson.chapterId)).size,
    lessonCount: new Set(lessons.map((lesson) => lesson.lessonId)).size,
  }
}

export function isReviewNotePreviewForSelection(
  preview: ReviewNotePreviewView | undefined,
  sourceLessonIds: number[],
): preview is ReviewNotePreviewView {
  if (preview == null || sourceLessonIds.length === 0) return false
  const selected = normalizeSourceLessonIds(sourceLessonIds)
  const actual = normalizeSourceLessonIds(preview.sourceLessons.map((lesson) => lesson.lessonId))
  return selected.length === actual.length && selected.every((id, index) => id === actual[index])
}

export interface ReviewNoteSourceNode {
  key: string
  title: string
  lessonIds: number[]
  selectableLessonIds: number[]
  children: ReviewNoteSourceNode[]
}

export function buildReviewNoteSourceTree(lessons: ReviewNoteSourceLessonView[]): ReviewNoteSourceNode[] {
  const roots: ReviewNoteSourceNode[] = []
  const nodes = new Map<string, ReviewNoteSourceNode>()
  const seenLessons = new Set<number>()
  const selectableIds = new Set(filterSelectableSourceLessonIds(lessons.map((lesson) => lesson.lessonId), lessons))
  for (const lesson of lessons) {
    if (seenLessons.has(lesson.lessonId)) continue
    seenLessons.add(lesson.lessonId)
    const groups = [
      [lesson.curriculumId, lesson.curriculumTitle],
      [lesson.courseId, lesson.courseTitle],
      [lesson.chapterId, lesson.chapterTitle],
    ] as const
    let children = roots
    let path = ''
    const ancestors: ReviewNoteSourceNode[] = []
    groups.forEach(([id, title], index) => {
      if (id == null || title == null) return
      path += `/${index}:${id}`
      let node = nodes.get(path)
      if (node == null) {
        node = { key: path, title, lessonIds: [], selectableLessonIds: [], children: [] }
        nodes.set(path, node)
        children.push(node)
      }
      ancestors.push(node)
      children = node.children
    })
    const selectable = selectableIds.has(lesson.lessonId)
    ancestors.forEach((node) => {
      node.lessonIds.push(lesson.lessonId)
      if (selectable) node.selectableLessonIds.push(lesson.lessonId)
    })
    children.push({
      key: `${path}/lesson:${lesson.lessonId}`,
      title: lesson.lessonTitle,
      lessonIds: [lesson.lessonId],
      selectableLessonIds: selectable ? [lesson.lessonId] : [],
      children: [],
    })
  }
  return roots
}

export function canExcludeReviewNoteItems(note: ReviewNoteView): boolean {
  return note.canEditItems && note.totalCount > 1 && note.items.length > 1
}
